/**
 * HeaderTab — extracted from SalesOrderCreate.js.
 * Receives all needed state + handlers via a single `ctx` prop bag
 * so the parent page retains single-source-of-truth.
 */
import React from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { TabsContent } from '../../components/ui/tabs';
import { AlertTriangle, CheckCircle2, FileText, Loader2, Shield, XCircle } from 'lucide-react';

const HeaderTab = ({ ctx }) => {
  const {
    header,
    setHeader,
    customers,
    salesOrgs,
    distChannels,
    availableSalesOrgs,
    availableDistChannels,
    availableDivisions,
    partnerFunctions,
    orderReasons,
    shippingConditions,
    eligibleQuotations,
    selectedQuotation,
    isLoadingQuotation,
    quotationLoaded,
    pricingProcedureSource,
    creditCheckResult,
    isCheckingCredit,
    handleSalesOrgChange,
    handleDistChannelChange,
    handleCreditCheck,
    handleCustomerChange,
    handleQuotationSelect,
    handleClearQuotationReference,
    deliveryBlockOptions,
  } = ctx;

  return (
          <TabsContent value="header">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Quotation Reference - Dropdown Card */}
              <Card className="p-6 lg:col-span-2 border-2 border-orange-200 bg-orange-50">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-orange-600" />
                  Reference Document
                </h3>
                <div className="flex items-end gap-4">
                  <div className="flex-1 max-w-xl">
                    <Label>Reference Quotation</Label>
                    <Select 
                      value={selectedQuotation || '__NONE__'} 
                      onValueChange={handleQuotationSelect}
                      disabled={isLoadingQuotation}
                    >
                      <SelectTrigger data-testid="quotation-reference-select">
                        <SelectValue placeholder="Select Quotation (Optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__NONE__">-- No Reference (Create New Order) --</SelectItem>
                        {eligibleQuotations.filter(q => q.is_eligible).length > 0 && (
                          <div className="px-2 py-1 text-xs font-semibold text-green-700 bg-green-50">
                            Eligible for Conversion
                          </div>
                        )}
                        {eligibleQuotations.filter(q => q.is_eligible).map(q => (
                          <SelectItem key={q.quotation_number} value={q.quotation_number}>
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{q.quotation_number}</span>
                              <span className="text-gray-500">|</span>
                              <span>{q.customer_name || q.customer_id}</span>
                              <span className="text-gray-500">|</span>
                              <span className="text-green-600">{q.item_count} item(s)</span>
                              <span className="text-xs text-gray-400">({q.status})</span>
                            </div>
                          </SelectItem>
                        ))}
                        {eligibleQuotations.filter(q => !q.is_eligible).length > 0 && (
                          <div className="px-2 py-1 text-xs font-semibold text-gray-500 bg-gray-50 mt-2">
                            Not Eligible
                          </div>
                        )}
                        {eligibleQuotations.filter(q => !q.is_eligible).map(q => (
                          <SelectItem key={q.quotation_number} value={q.quotation_number} disabled>
                            <div className="flex items-center gap-2 opacity-60">
                              <span>{q.quotation_number}</span>
                              <span className="text-gray-500">|</span>
                              <span>{q.customer_name || q.customer_id}</span>
                              <span className="text-xs text-red-500">({q.validity_note})</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-600 mt-1">
                      {isLoadingQuotation ? (
                        <span className="flex items-center gap-1">
                          <Loader2 className="w-3 h-3 animate-spin" />
                          Loading quotation data...
                        </span>
                      ) : quotationLoaded ? (
                        <span className="text-green-600">✓ Data loaded from Quotation {selectedQuotation}</span>
                      ) : (
                        `${eligibleQuotations.filter(q => q.is_eligible).length} quotation(s) available for conversion`
                      )}
                    </p>
                  </div>
                  {quotationLoaded && (
                    <Button 
                      onClick={handleClearQuotationReference}
                      variant="outline"
                      size="sm"
                    >
                      Clear Selection
                    </Button>
                  )}
                </div>
              </Card>
              
              {/* Customer & Organization */}
              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-4">Customer & Organization</h3>
                <div className="space-y-4">
                  <div>
                    <Label>Document Type *</Label>
                    <Select value={header.document_type} onValueChange={(v) => setHeader({...header, document_type: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="OR">OR - Standard Order</SelectItem>
                        <SelectItem value="RO">RO - Rush Order</SelectItem>
                        <SelectItem value="CS">CS - Cash Sale</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Sold-to Party (Customer) *</Label>
                    <Select 
                      value={header.sold_to_party || '__SELECT__'} 
                      onValueChange={(v) => handleCustomerChange(v === '__SELECT__' ? '' : v)}
                      disabled={quotationLoaded}
                    >
                      <SelectTrigger><SelectValue placeholder="Select Customer" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__SELECT__">Select Customer</SelectItem>
                        {customers.map(c => (
                          <SelectItem key={c.customer_id} value={c.customer_id}>
                            {c.customer_id} - {c.name || c.customer_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Sales Organization *</Label>
                      <Select 
                        value={header.sales_organization || '__SELECT__'} 
                        onValueChange={(v) => handleSalesOrgChange(v === '__SELECT__' ? '' : v)}
                        disabled={quotationLoaded || !header.sold_to_party}
                      >
                        <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__SELECT__">Select Sales Org</SelectItem>
                          {(availableSalesOrgs.length > 0 ? availableSalesOrgs : salesOrgs).map(org => (
                            <SelectItem key={org.sales_org_id} value={org.sales_org_id}>
                              {org.sales_org_id} - {org.sales_org_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {header.sold_to_party && availableSalesOrgs.length === 0 && !quotationLoaded && (
                        <p className="text-xs text-amber-600 mt-1">No sales area data for this customer</p>
                      )}
                    </div>
                    <div>
                      <Label>Distribution Channel *</Label>
                      <Select 
                        value={header.distribution_channel || '__SELECT__'} 
                        onValueChange={(v) => handleDistChannelChange(v === '__SELECT__' ? '' : v)}
                        disabled={quotationLoaded || !header.sales_organization}
                      >
                        <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__SELECT__">Select Channel</SelectItem>
                          {(availableDistChannels.length > 0 ? availableDistChannels : distChannels).map(dc => (
                            <SelectItem key={dc.dist_channel_id} value={dc.dist_channel_id}>
                              {dc.dist_channel_id} - {dc.dist_channel_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {header.sales_organization && availableDistChannels.length === 0 && !quotationLoaded && (
                        <p className="text-xs text-amber-600 mt-1">No channels for selected sales org</p>
                      )}
                    </div>
                  </div>
                  {availableDivisions.length > 0 && (
                    <div>
                      <Label>Division</Label>
                      <Select 
                        value={header.division || '__SELECT__'} 
                        onValueChange={(v) => setHeader({...header, division: v === '__SELECT__' ? '' : v})}
                        disabled={quotationLoaded}
                      >
                        <SelectTrigger><SelectValue placeholder="Select Division" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__SELECT__">Select Division</SelectItem>
                          {availableDivisions.map(div => (
                            <SelectItem key={div.division_id} value={div.division_id}>
                              {div.division_id} {div.division_name !== div.division_id ? `- ${div.division_name}` : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div>
                    <Label>Pricing Procedure</Label>
                    <Input
                      value={header.pricing_procedure || ''}
                      readOnly
                      className="bg-gray-100"
                      placeholder="Auto-fetched from customer master"
                      data-testid="pricing-procedure-display"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {pricingProcedureSource === 'customer_master' && '✓ From Customer Master'}
                      {pricingProcedureSource === 'quotation' && '✓ From Reference Quotation'}
                      {pricingProcedureSource === 'default' && 'Using default pricing procedure'}
                      {!pricingProcedureSource && 'Will be determined from customer/quotation'}
                    </p>
                  </div>
                </div>
              </Card>

              {/* Partner Functions */}
              {header.sold_to_party && (
                <Card className="p-6 lg:col-span-2 border border-orange-200" data-testid="partner-functions-card">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    Partner Functions
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Specify different parties for shipping, billing, and payment. By default, all partners are set to the Sold-to Party.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {/* Sold-to Party (Read-only) */}
                    <div>
                      <Label className="flex items-center gap-1">
                        <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                        Sold-to Party (SP)
                      </Label>
                      <div className="mt-1 p-3 bg-gray-100 rounded-md">
                        <p className="font-medium text-sm">{partnerFunctions.sold_to?.name || header.sold_to_party}</p>
                        <p className="text-xs text-gray-500">{header.sold_to_party}</p>
                        {partnerFunctions.sold_to?.city && (
                          <p className="text-xs text-gray-400">{partnerFunctions.sold_to.city}</p>
                        )}
                      </div>
                    </div>
                    
                    {/* Ship-to Party */}
                    <div>
                      <Label className="flex items-center gap-1">
                        <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                        Ship-to Party (SH)
                      </Label>
                      <Select 
                        value={header.ship_to_party || header.sold_to_party || '__SELECT__'}
                        onValueChange={(v) => setHeader({...header, ship_to_party: v === '__SELECT__' ? header.sold_to_party : v})}
                        disabled={quotationLoaded}
                      >
                        <SelectTrigger data-testid="ship-to-party-select">
                          <SelectValue placeholder="Select Ship-to" />
                        </SelectTrigger>
                        <SelectContent>
                          {partnerFunctions.ship_to_parties.length > 0 ? (
                            partnerFunctions.ship_to_parties.map(p => (
                              <SelectItem key={p.customer_id} value={p.customer_id}>
                                {p.customer_id} - {p.name} {p.is_default ? '(Same as Sold-to)' : `(${p.city || ''})`}
                              </SelectItem>
                            ))
                          ) : (
                            <SelectItem value={header.sold_to_party}>
                              {header.sold_to_party} (Same as Sold-to)
                            </SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-gray-500 mt-1">Where goods will be delivered</p>
                    </div>
                    
                    {/* Bill-to Party */}
                    <div>
                      <Label className="flex items-center gap-1">
                        <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
                        Bill-to Party (BP)
                      </Label>
                      <Select 
                        value={header.bill_to_party || header.sold_to_party || '__SELECT__'}
                        onValueChange={(v) => setHeader({...header, bill_to_party: v === '__SELECT__' ? header.sold_to_party : v})}
                        disabled={quotationLoaded}
                      >
                        <SelectTrigger data-testid="bill-to-party-select">
                          <SelectValue placeholder="Select Bill-to" />
                        </SelectTrigger>
                        <SelectContent>
                          {partnerFunctions.bill_to_parties.length > 0 ? (
                            partnerFunctions.bill_to_parties.map(p => (
                              <SelectItem key={p.customer_id} value={p.customer_id}>
                                {p.customer_id} - {p.name} {p.is_default ? '(Same as Sold-to)' : `(${p.city || ''})`}
                              </SelectItem>
                            ))
                          ) : (
                            <SelectItem value={header.sold_to_party}>
                              {header.sold_to_party} (Same as Sold-to)
                            </SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-gray-500 mt-1">Who receives the invoice</p>
                    </div>
                    
                    {/* Payer */}
                    <div>
                      <Label className="flex items-center gap-1">
                        <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                        Payer (PY)
                      </Label>
                      <Select 
                        value={header.payer || header.sold_to_party || '__SELECT__'}
                        onValueChange={(v) => setHeader({...header, payer: v === '__SELECT__' ? header.sold_to_party : v})}
                        disabled={quotationLoaded}
                      >
                        <SelectTrigger data-testid="payer-select">
                          <SelectValue placeholder="Select Payer" />
                        </SelectTrigger>
                        <SelectContent>
                          {partnerFunctions.payers.length > 0 ? (
                            partnerFunctions.payers.map(p => (
                              <SelectItem key={p.customer_id} value={p.customer_id}>
                                {p.customer_id} - {p.name} {p.is_default ? '(Same as Sold-to)' : `(${p.city || ''})`}
                              </SelectItem>
                            ))
                          ) : (
                            <SelectItem value={header.sold_to_party}>
                              {header.sold_to_party} (Same as Sold-to)
                            </SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-gray-500 mt-1">Who makes the payment</p>
                    </div>
                  </div>
                  
                  {/* Partner summary */}
                  {(header.ship_to_party !== header.sold_to_party || 
                    header.bill_to_party !== header.sold_to_party || 
                    header.payer !== header.sold_to_party) && (
                    <div className="mt-4 p-3 bg-orange-50 rounded-lg text-sm">
                      <p className="font-medium text-orange-800">Different Partner Configuration:</p>
                      <ul className="mt-1 text-orange-700 text-xs space-y-0.5">
                        {header.ship_to_party !== header.sold_to_party && (
                          <li>• Ship-to: {header.ship_to_party}</li>
                        )}
                        {header.bill_to_party !== header.sold_to_party && (
                          <li>• Bill-to: {header.bill_to_party}</li>
                        )}
                        {header.payer !== header.sold_to_party && (
                          <li>• Payer: {header.payer}</li>
                        )}
                      </ul>
                    </div>
                  )}
                </Card>
              )}

              {/* Dates & References */}
              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-4">Dates & References</h3>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Document Date *</Label>
                      <Input
                        type="date"
                        value={header.document_date}
                        onChange={(e) => setHeader({...header, document_date: e.target.value})}
                        required
                      />
                    </div>
                    <div>
                      <Label>Header Requested Delivery Date</Label>
                      <Input
                        type="date"
                        value={header.requested_delivery_date}
                        min={new Date().toISOString().split('T')[0]}
                        onChange={(e) => {
                          const selectedDate = e.target.value;
                          const today = new Date().toISOString().split('T')[0];
                          if (selectedDate && selectedDate < today) {
                            toast.error('Requested Delivery Date cannot be in the past');
                            return;
                          }
                          setHeader({...header, requested_delivery_date: selectedDate});
                        }}
                      />
                      <p className="text-xs text-gray-500 mt-1">Default for all items (Item-level date takes priority)</p>
                    </div>
                  </div>
                  <div>
                    <Label>Customer Reference (PO Number)</Label>
                    <Input
                      value={header.customer_reference}
                      onChange={(e) => setHeader({...header, customer_reference: e.target.value})}
                      placeholder="Customer's PO number"
                      maxLength={35}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Payment Terms</Label>
                      <Select value={header.payment_terms || '__NONE__'} onValueChange={(v) => setHeader({...header, payment_terms: v === '__NONE__' ? '' : v})}>
                        <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__NONE__">-- None --</SelectItem>
                          <SelectItem value="NT30">Net 30 Days</SelectItem>
                          <SelectItem value="NT60">Net 60 Days</SelectItem>
                          <SelectItem value="NT90">Net 90 Days</SelectItem>
                          <SelectItem value="NET30">NET30 - Net 30 Days</SelectItem>
                          <SelectItem value="COD">Cash on Delivery</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Incoterms</Label>
                      <Select value={header.incoterms || '__NONE__'} onValueChange={(v) => setHeader({...header, incoterms: v === '__NONE__' ? '' : v})}>
                        <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__NONE__">-- None --</SelectItem>
                          <SelectItem value="EXW">EXW - Ex Works</SelectItem>
                          <SelectItem value="FOB">FOB - Free on Board</SelectItem>
                          <SelectItem value="CIF">CIF - Cost, Insurance, Freight</SelectItem>
                          <SelectItem value="DDP">DDP - Delivered Duty Paid</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label>Incoterms Location</Label>
                    <Input
                      value={header.incoterms_location || ''}
                      onChange={(e) => setHeader({...header, incoterms_location: e.target.value})}
                      placeholder="e.g., New York, Los Angeles"
                      maxLength={100}
                    />
                  </div>
                  <div>
                    <Label>Header Note</Label>
                    <textarea
                      value={header.header_note}
                      onChange={(e) => setHeader({...header, header_note: e.target.value})}
                      className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm resize-none"
                      rows={3}
                      placeholder="Additional notes or instructions..."
                      maxLength={500}
                    />
                  </div>
                </div>
              </Card>
              
              {/* Shipping & Order Processing */}
              <Card className="p-6" data-testid="shipping-order-processing-card">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                  </svg>
                  Shipping & Order Processing
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label>Order Reason</Label>
                    <Select 
                      value={header.order_reason || '__NONE__'} 
                      onValueChange={(v) => setHeader({...header, order_reason: v === '__NONE__' ? '' : v})}
                      disabled={quotationLoaded}
                    >
                      <SelectTrigger data-testid="order-reason-select">
                        <SelectValue placeholder="Select Order Reason" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__NONE__">-- None --</SelectItem>
                        {orderReasons.map(or => (
                          <SelectItem key={or.code} value={or.code}>
                            {or.code} - {or.description}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-500 mt-1">Reason for placing this order</p>
                  </div>
                  <div>
                    <Label>Shipping Conditions</Label>
                    <Select 
                      value={header.shipping_conditions || '__NONE__'} 
                      onValueChange={(v) => setHeader({...header, shipping_conditions: v === '__NONE__' ? '' : v})}
                    >
                      <SelectTrigger data-testid="shipping-conditions-select">
                        <SelectValue placeholder="Select Shipping Conditions" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__NONE__">-- None --</SelectItem>
                        {shippingConditions.map(sc => (
                          <SelectItem key={sc.shipping_conditions_id} value={sc.shipping_conditions_id}>
                            {sc.shipping_conditions_id} - {sc.description}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-500 mt-1">Shipping method requirements</p>
                  </div>
                  <div>
                    <Label>Delivery Block</Label>
                    <Select 
                      value={header.delivery_block || '__NONE__'} 
                      onValueChange={(v) => setHeader({...header, delivery_block: v === '__NONE__' ? '' : v})}
                    >
                      <SelectTrigger data-testid="delivery-block-select">
                        <SelectValue placeholder="No Block" />
                      </SelectTrigger>
                      <SelectContent>
                        {deliveryBlockOptions.map(db => (
                          <SelectItem key={db.code || '__NONE__'} value={db.code || '__NONE__'}>
                            {db.code ? `${db.code} - ${db.description}` : db.description}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-500 mt-1">Block delivery if required</p>
                  </div>
                </div>
                {header.delivery_block && header.delivery_block !== '' && (
                  <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-600" />
                    <span className="text-sm text-red-700 font-medium">
                      Delivery Block Active: {deliveryBlockOptions.find(d => d.code === header.delivery_block)?.description || header.delivery_block}
                    </span>
                  </div>
                )}
              </Card>
            </div>
            
            {/* Credit Check Section */}
            <Card className="p-6 mt-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  Credit Check
                </h3>
                <Button 
                  variant="outline" 
                  onClick={handleCreditCheck} 
                  disabled={!header.sold_to_party || isCheckingCredit}
                  data-testid="check-credit-btn"
                >
                  {isCheckingCredit ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Checking...</>
                  ) : (
                    <><Shield className="w-4 h-4 mr-2" /> Check Credit</>
                  )}
                </Button>
              </div>
              
              {creditCheckResult ? (
                <div className={`p-4 rounded-lg border ${
                  creditCheckResult.status === 'APPROVED' ? 'bg-green-50 border-green-200' :
                  creditCheckResult.status === 'WARNING' ? 'bg-yellow-50 border-yellow-200' :
                  'bg-red-50 border-red-200'
                }`}>
                  <div className="flex items-center gap-3 mb-3">
                    {creditCheckResult.status === 'APPROVED' ? (
                      <CheckCircle2 className="w-6 h-6 text-green-600" />
                    ) : creditCheckResult.status === 'WARNING' ? (
                      <AlertTriangle className="w-6 h-6 text-yellow-600" />
                    ) : (
                      <XCircle className="w-6 h-6 text-red-600" />
                    )}
                    <span className={`font-semibold ${
                      creditCheckResult.status === 'APPROVED' ? 'text-green-800' :
                      creditCheckResult.status === 'WARNING' ? 'text-yellow-800' :
                      'text-red-800'
                    }`}>
                      {creditCheckResult.status === 'APPROVED' ? 'Credit Approved' :
                       creditCheckResult.status === 'WARNING' ? 'Credit Warning' :
                       'Credit Blocked'}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-gray-600">Credit Limit</p>
                      <p className="font-mono font-medium">${creditCheckResult.credit_limit?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '0.00'}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Current Exposure</p>
                      <p className="font-mono font-medium">${creditCheckResult.current_exposure?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '0.00'}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">New Order Value</p>
                      <p className="font-mono font-medium">${creditCheckResult.new_order_value?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '0.00'}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Available Credit</p>
                      <p className={`font-mono font-medium ${creditCheckResult.available_credit < 0 ? 'text-red-600' : 'text-green-600'}`}>
                        ${creditCheckResult.available_credit?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '0.00'}
                      </p>
                    </div>
                  </div>
                  <p className="mt-3 text-sm">{creditCheckResult.message}</p>
                </div>
              ) : (
                <div className="p-4 bg-gray-50 rounded-lg text-center text-gray-500">
                  <Shield className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                  <p>Select a customer and click "Check Credit" to verify credit availability</p>
                </div>
              )}
            </Card>
          </TabsContent>
  );
};

export default HeaderTab;
